// services/notification.service.ts
import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarMessageComponent } from '../app/apps/shared/snackbar-message/snackbar-message.component';
import { Router } from '@angular/router';
import { DesktopTitleService } from './desktop-title.service';
import { GlobalPollingService, NotificationData, ChatMessageToastDto, AppNotificationCounts } from './global-polling.service';
import { BadgeService } from '../app/services/badge.service';

export { NotificationData, ChatMessageToastDto, AppNotificationCounts };

@Injectable({
  providedIn: 'root'
})
export class SSENotificationService {
  constructor(
    private ngZone: NgZone,
    private snackBar: MatSnackBar,
    private router: Router,
    private desktopTitleService: DesktopTitleService,
    private globalPollingService: GlobalPollingService,
    private badgeService: BadgeService
  ) {
    // Subscribe to notification updates from global polling service
    this.globalPollingService.getNotifications().subscribe(data => {
      if (data) {
        this.handleNotificationUpdate(data, false);
      }
    });
  }

  async connect(userId: number): Promise<void> {
    // Delegate to global polling service
    return this.globalPollingService.connect(userId);
  }

  private handleNotificationUpdate(data: NotificationData, isResuming: boolean): void {
    if (data.MessageCount != undefined) {
      // Update desktop favicon badge (web)
      this.desktopTitleService.setNotificationCount(data.MessageCount);

      // Update mobile app icon badge (iOS/Android)
      this.badgeService.setBadgeCount(data.MessageCount);
    }

    // Only show individual message toast if not resuming from background
    if (data.NewMessage && !isResuming && !this.router.url.startsWith("/apps/messages")) {
      this.snackBar.openFromComponent(SnackbarMessageComponent, {
        data: data.NewMessage,
        duration: DesktopTitleService.MESSAGE_DURATION,
        panelClass: 'snackbar-on-top',
        verticalPosition: 'bottom',
        horizontalPosition: 'center'
      });

      this.desktopTitleService.setTitle("You have a new message!");
    }
  }

  disconnect(): void {
    // Global service manages the connection, individual services don't disconnect
    // to avoid affecting other subscribers
  }

  pausePolling(): void {
    this.globalPollingService.pausePolling();
  }

  resumePolling(): void {
    this.globalPollingService.resumePolling();
  }

  getNotifications(): Observable<NotificationData | null> {
    return this.globalPollingService.getNotifications();
  }

  /** Per-app badge counts keyed by AppId, for the header/footer nav row. */
  getAppCounts(): Observable<AppNotificationCounts | null> {
    return this.globalPollingService.getAppCounts();
  }

  getConnectionStatus(): Observable<boolean> {
    return this.globalPollingService.getConnectionStatus();
  }

  isConnected(): boolean {
    return this.globalPollingService.isConnected();
  }
}
